import { isDefined } from "@clipboard-health/util-ts";
import { formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { convertToGeoLocation } from "@src/appV2/Location";
import { useGetQualificationDisplayLabels } from "@src/appV2/Qualifications/api/useGetQualificationDisplayLabels";
import { useShiftInviteActions } from "@src/appV2/redesign/WorkerShifts/ShiftInvites/api/useShiftInviteActions";
import { useShiftInviteItemContext } from "@src/appV2/redesign/WorkerShifts/ShiftInvites/context/shiftInviteItemContext";
import { parseISO } from "date-fns";

import { useCurrentDate } from "../../../lib/utils/useCurrentDate";
import { useDefinedWorker } from "../../../Worker/useDefinedWorker";
import { useShiftRouteEstimate } from "../../ArrivalTime/ShiftRouteEstimate/useShiftRouteEstimate";
import { useIsArrivalTimeEligible } from "../../ArrivalTime/useIsArrivalTimeEligible";
import { type TimeSlot } from "../../Shift/Open/types";
import { calculateTotalPay } from "../../utils/calculateShiftPay";
import { getShiftWorkDurationInHours } from "../../utils/getShiftWorkDuration";
import { useDistanceToWorkplace } from "../../Workplace/useDistanceToWorkplace";
import { ShiftInviteCard } from "./ShiftInviteCard";
import { type ShiftInvite } from "./types";

interface ShiftInviteCardContainerProps {
  shiftInvite: ShiftInvite;
  isHighlighted?: boolean;
}

export function ShiftInviteCardContainer(props: ShiftInviteCardContainerProps) {
  const { shiftInvite, isHighlighted } = props;
  const {
    attributes: {
      shiftDetails: {
        name,
        start: shiftStartIso,
        end: shiftEndIso,
        agentReq,
        pay,
        tmz: shiftTimezone,
      },
      facility: {
        name: workplaceName,
        geoLocation: { coordinates },
        type: workplaceType,
        requiresLunchBreak,
      },
      workplaceId,
      breakPolicy,
      expiresAt: expiresAtIso,
      status,
    },
    id: shiftInviteId,
  } = shiftInvite;

  const worker = useDefinedWorker();
  const { arrivalTimeModalState } = useShiftInviteItemContext();

  const { qualificationNameToDisplayLabelMap } = useGetQualificationDisplayLabels([agentReq]);
  const qualificationDisplayLabel = qualificationNameToDisplayLabelMap.get(agentReq) ?? agentReq;

  const {
    acceptShiftInvite,
    declineShiftInvite,
    isAccepting: isAcceptingAnyInvite,
    isDeclining: isDecliningAnyInvite,
    isSuccessAcceptOrDecline,
    acceptOrDeclineRequest,
  } = useShiftInviteActions();

  const isAccepting =
    isAcceptingAnyInvite && acceptOrDeclineRequest?.shiftInviteId === shiftInviteId;
  const isDeclining =
    isDecliningAnyInvite && acceptOrDeclineRequest?.shiftInviteId === shiftInviteId;

  const { formattedDistance } = useDistanceToWorkplace({
    workplaceGeoLocation: convertToGeoLocation(coordinates),
  });

  const shiftStart = parseISO(shiftStartIso);
  const shiftEnd = parseISO(shiftEndIso);
  const expiresAt = parseISO(expiresAtIso);

  const isShortLeadTime = useIsArrivalTimeEligible({
    shiftStartIso,
    workplaceType,
  });

  const requiresBreak = (requiresLunchBreak ?? false) && isDefined(breakPolicy);
  const shiftWorkDurationInHours = getShiftWorkDurationInHours(
    { start: shiftStartIso, end: shiftEndIso },
    requiresBreak,
    requiresBreak ? (breakPolicy?.breakDurationInMinutes ?? 0) : 0
  );

  const hourlyPay = formatDollarsAsUsd(pay ?? 0);
  const { formattedPayAmount: totalPay } = calculateTotalPay({
    hourlyPayRate: pay ?? 0,
    hoursWorked: shiftWorkDurationInHours,
  });

  const {
    data: computedArrivalData,
    isLoading: isLoadingEta,
    fetchStatus,
  } = useShiftRouteEstimate(
    {
      destination: {
        longitude: coordinates[0],
        latitude: coordinates[1],
      },
      workerId: worker.userId,
      shiftInviteId,
      workplaceId,
      shiftStartIso,
    },
    {
      enabled: isShortLeadTime,
    }
  );
  const isEnabledAndLoading = isLoadingEta && fetchStatus !== "idle";

  const currentDate = useCurrentDate(1000);

  return (
    <ShiftInviteCard
      timeSlot={name as TimeSlot}
      shiftStart={shiftStart}
      shiftEnd={shiftEnd}
      currentDate={currentDate}
      expiresAt={expiresAt}
      formattedDistance={formattedDistance ?? ""}
      shiftTimezone={shiftTimezone ?? ""}
      qualificationText={qualificationDisplayLabel}
      isLoadingQualification={false}
      workplaceName={workplaceName}
      shiftWorkDurationInHours={shiftWorkDurationInHours}
      hourlyPay={hourlyPay}
      totalPay={totalPay}
      isAccepting={isAccepting}
      isDeclining={isDeclining}
      isSuccessProcessingInvite={isSuccessAcceptOrDecline}
      isShortLeadTime={isShortLeadTime}
      isHighlighted={isHighlighted}
      isLoadingRouteEstimate={isEnabledAndLoading}
      status={status}
      onAccept={async () => {
        if (isShortLeadTime && isDefined(computedArrivalData)) {
          arrivalTimeModalState.openModal();
        } else {
          await acceptShiftInvite({ shiftInvite });
        }
      }}
      onDecline={async () => {
        await declineShiftInvite({ shiftInviteId });
      }}
    />
  );
}
